import { type FormEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import {
  FulfillmentAdminClient,
  type FulfillmentOrderDetail,
  type PackingRecordBody,
  type PackingRecordRecording,
  type PackingStopSignal,
  type PackingRecordSaved,
  type PackingScanLogEntry,
} from '@/infrastructure/FulfillmentAdminClient';
import { StockClient } from '@/infrastructure/StockClient';
import { ROUTES } from '@/routes/routes';
import { kstClock } from '@/utils/fulfillmentTime';
import { toScanAscii } from '@/utils/scanAscii';
import { CameraScanField } from '../components/CameraScanField';
import { asciiScanField } from '../components/asciiScanField';
import { speak } from '../components/fulfillmentVoice';
import { FulfillmentSound } from '../components/outboundSound';
import { CheckIcon, CloseIcon, RefreshIcon, ScanIcon, VideoIcon, WarningIcon } from '../components/UltimateIcons';
import { SCAN_SETTLED_MS, useScanInput } from '../components/useScanInput';
import {
  NO_BARCODE_CODE,
  SAMPLE_SET_CODE,
  SAMPLE_SET_KEY,
  appendScanLog,
  buildCheckRows,
  codeKey,
  countOne,
  isComplete,
  looksLikeOrderNumber,
  matchedLabel,
  newRecordId,
  noBarcodeRows,
  orderNotices,
  outboundLabelCodes,
  recordProducts,
  rowsByCode,
  sharedBarcodeKeys,
  uncountOne,
  unitsOf,
  type CheckRow,
  type CountMethod,
  type PackingCatalogEntry,
} from './packingCheckRules';
import { readCheckedToday, recordChecked, writeCheckedToday, type CheckedOrder } from './packingCheckLog';
import {
  readPendingRecords,
  withPendingRecord,
  withoutPendingRecord,
  writePendingRecords,
  type PendingPackingRecord,
} from './packingRecordQueue';
import {
  ZERO_PACKING_STOP,
  ZERO_PACKING_STOP_GAP_MS,
  describeSignalFailure,
  probeZeroPacking,
  readZeroPackingEnabled,
  sendZeroPackingSignal,
  writeZeroPackingEnabled,
  type SignalAnswer,
} from './zeroPackingRecorder';
import '../wireframe.css';
import '../ultimateDesign.css';
import './packingCheck.css';

/** Coupang's small QR hands the scanner `[V1]<code>`; the code after it is what was printed. */
const COUPANG_QR_PREFIX = /^\[V1\]/i;
const scannedCode = (raw: string): string => toScanAscii(raw).trim().replace(COUPANG_QR_PREFIX, '');

/** The order on the desk: what has to go in, what is in, and the order the units were counted in (for undo). */
interface Session {
  detail: FulfillmentOrderDetail;
  rows: CheckRow[];
  /** False when the catalog could not be read: barcodes are then unknown, and every scan asks the server. */
  catalogLoaded: boolean;
  counted: string[];
  /** How each unit in `counted` was counted, in the same order. */
  countedBy: CountMethod[];
  lastKey: string | null;
  completedAt: string | null;
  /** Made once per box; the server keeps a resent record once (SSD-7587). */
  recordId: string;
  startedAt: string;
  recording: PackingRecordRecording;
  /** The carrier label scanned off the closed box. Once set, the box is done and the order takes no more counts. */
  label: { code: string; at: string } | null;
  save: 'SAVING' | 'SAVED' | 'PENDING' | null;
}

/** The one line under the scan box that says what the last scan did. */
interface Flash {
  tone: 'ok' | 'bad' | 'warn' | 'info' | 'done';
  title: string;
  text?: string | undefined;
  code?: string | undefined;
  /** The product a count landed on, so its picture shows next to the answer without looking down at the table. */
  imageUrl?: string | null | undefined;
  at: string;
}

/**
 * What this screen believes the Zero Packing app on this PC is doing. UNKNOWN follows a signal that got no answer:
 * it may have arrived, so nothing is resent until the packer has looked at the mini window.
 */
type RecorderStatus = 'OFF' | 'IDLE' | 'RECORDING' | 'UNKNOWN';

interface RecorderState {
  status: RecorderStatus;
  orderId: number | null;
  since: string | null;
  message: string | null;
}

type LinkCheck = 'idle' | 'checking' | 'up' | 'down';

/** How long the second scan of another order has to leave a box with units already in. */
const SWITCH_CONFIRM_MS = 10000;

/** After the browser comes back, how long a returning click has to land on the away cover before it lifts by itself. */
const AWAY_SETTLE_MS = 400;
/** How long the lifted cover stays, clear, to take the second press of a double click. */
const AWAY_LIFT_MS = 500;

/** The away cover: off, up (the browser lost the keyboard), or lifting (clear for a moment, still taking clicks). */
type AwayState = 'HERE' | 'AWAY' | 'LIFTING';

const stamp = (): string => new Date().toISOString();
const pause = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const errorText = (err: unknown): string => {
  if (isAxiosError<{ message?: string | string[] }>(err)) {
    const message = err.response?.data?.message;
    const text = Array.isArray(message) ? message.join(', ') : message;
    return text ? `${err.response?.status ?? ''} ${text}`.trim() : err.message;
  }
  return err instanceof Error ? err.message : String(err);
};

/** A refusal that sending again will not fix: a 4xx other than a timeout, a rate limit or a sign-in problem. */
const isPermanentRefusal = (err: unknown): boolean => {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    // A timeout, a rate limit or an expired sign-in clears up on its own; those are sent again automatically.
    ![401, 403, 408, 429].includes(status)
  );
};

const NOT_A_PRODUCT_HINT =
  'Scan the barcode printed on the product itself, not the parcel label or the outer box. If the product has no barcode, ask the supervisor to check it and scan the no-barcode card.';

/** The record the server keeps for a box whose label was scanned. */
const recordBodyOf = (session: Session, scans: readonly PackingScanLogEntry[]): PackingRecordBody => {
  const label = session.label ?? { code: '', at: stamp() };
  const units = unitsOf(session.rows);
  return {
    recordId: session.recordId,
    startedAt: session.startedAt,
    checkedAt: session.completedAt ?? label.at,
    labelScannedAt: label.at,
    label: label.code.slice(0, 128),
    expectedUnits: units.expected,
    scannedUnits: units.scanned,
    products: recordProducts(session.rows, session.counted, session.countedBy),
    recording: session.recording,
    scans: [...scans],
  };
};

const elapsed = (sinceIso: string | null, nowMs: number): string => {
  const seconds = sinceIso ? Math.max(0, Math.floor((nowMs - new Date(sinceIso).getTime()) / 1000)) : 0;
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * A desk card with one shared code, drawn here so the desk can print it from this screen. A mouse click on it,
 * opening or shutting it included, hands the cursor back to the scan box so the next scan lands there (DY 10/23).
 * A keyboard toggle keeps its focus, and a click that ends a text selection (copying the code) is left alone.
 */
function ScanCard({
  code,
  title,
  children,
  onUsed,
}: {
  code: string;
  title: string;
  children: ReactNode;
  onUsed: () => void;
}) {
  const ref = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    if (!ref.current) {
      return;
    }
    try {
      JsBarcode(ref.current, code, {
        format: 'CODE128',
        width: 2,
        height: 64,
        margin: 12,
        displayValue: false,
      });
    } catch {
      // A browser that cannot draw it still shows the code in text below.
    }
  }, [code]);
  return (
    <details
      className="pc-card"
      onClick={(event) => {
        if (event.detail > 0 && (window.getSelection()?.isCollapsed ?? true)) {
          onUsed();
        }
      }}
    >
      <summary>{title}</summary>
      <p>{children}</p>
      <svg ref={ref} className="pc-card-bars" aria-label={`Barcode ${code}`} />
      <code>{code}</code>
    </details>
  );
}

function FlashLine({ flash }: { flash: Flash | null }) {
  if (!flash) {
    return (
      <div className="pc-flash pc-flash-idle" data-testid="packing-flash">
        Scan the order number on the PACKING sheet to start.
      </div>
    );
  }
  const Icon =
    flash.tone === 'ok' || flash.tone === 'done' ? CheckIcon : flash.tone === 'bad' ? CloseIcon : WarningIcon;
  return (
    <div
      className={`pc-flash pc-flash-${flash.tone}`}
      role={flash.tone === 'bad' ? 'alert' : 'status'}
      data-testid="packing-flash"
      data-tone={flash.tone}
    >
      {flash.imageUrl ? (
        <img className="pc-flash-thumb" src={flash.imageUrl} alt="" />
      ) : (
        <Icon className="pc-flash-icon" />
      )}
      <div className="pc-flash-body">
        <b>{flash.title}</b>
        {flash.text && <span className="pc-flash-text">{flash.text}</span>}
      </div>
      <span className="pc-flash-meta">
        {flash.code && <code>{flash.code}</code>} {kstClock(flash.at)}
      </span>
    </div>
  );
}

function RowState({ row }: { row: CheckRow }) {
  if (row.scanned >= row.expected) {
    return (
      <span className="pc-state pc-state-done" aria-label="Checked">
        <CheckIcon />
      </span>
    );
  }
  return <span className={`pc-state ${row.scanned > 0 ? 'pc-state-part' : 'pc-state-none'}`} aria-label="Not yet" />;
}

type StepState = 'done' | 'now' | 'todo' | 'bad' | 'off';

/** The desk's five steps in one strip, so the packer sees what comes next from an arm's length. */
function PackingSteps({ session, recorder }: { session: Session | null; recorder: RecorderState }) {
  const open = session !== null;
  const blocked = session ? orderNotices(session.detail, session.rows).some((notice) => notice.tone === 'bad') : false;
  const recordingStep: StepState = !session
    ? recorder.status === 'OFF'
      ? 'off'
      : 'todo'
    : recorder.status === 'OFF' || !session.recording.enabled
      ? 'off'
      : session.recording.start === 'OK' || session.recording.start === 'CONFIRMED_BY_HAND'
        ? 'done'
        : blocked
          ? 'todo'
          : session.recording.start === 'FAILED' || recorder.status === 'UNKNOWN'
            ? 'bad'
            : 'now';
  const checked = session?.completedAt != null;
  const labelled = session?.label != null;
  const steps: Array<{ name: string; state: StepState }> = [
    { name: 'Order number', state: open ? (blocked ? 'bad' : 'done') : 'now' },
    { name: recordingStep === 'off' ? 'Recording (off)' : 'Recording', state: recordingStep },
    { name: 'Every product', state: !open || blocked ? 'todo' : checked ? 'done' : 'now' },
    { name: 'Pack the box', state: labelled ? 'done' : checked ? 'now' : 'todo' },
    { name: 'Carrier label', state: labelled ? 'done' : checked ? 'now' : 'todo' },
  ];
  return (
    <ol className="pc-steps" aria-label="Packing steps">
      {steps.map((step, index) => (
        <li key={step.name} className={`pc-step pc-step-${step.state}`} data-state={step.state}>
          <span className="pc-step-n">{step.state === 'done' ? <CheckIcon /> : index + 1}</span>
          <span className="pc-step-name">{step.name}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Fulfillment › Packing (SSD-7528, SSD-7587). The packing desk in one screen: the order number on the PACKING sheet
 * opens the order and starts the Zero Packing recording on this PC, every product is scanned (a product of the order
 * ticks green, anything else turns the screen red), and once the box is packed the carrier label scanned off it stops
 * the recording and keeps the packing record on the order. Zero Packing's PC app takes start and stop over its local
 * link in mini mode (`zeroPackingRecorder`); its partner API only reads videos. Nothing here changes inbound,
 * outbound, location or status: the only write is the PACKING_RECORDED event.
 */
export default function FulfillmentPackingCheckPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const stock = useMemo(() => new StockClient(), []);
  const scanInput = useScanInput({ preventScroll: true });
  const [code, setCode] = useState('');
  const [session, setSession] = useState<Session | null>(null);
  const [flash, setFlash] = useState<Flash | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const stickyRef = useRef<HTMLDivElement | null>(null);

  // The scan box and its answer stay pinned at the top while the products scroll under them (DY 10/22: the product
  // just scanned was below the fold). The pinned height is measured so a scrolled-to row lands just under it.
  useEffect(() => {
    const sticky = stickyRef.current;
    const root = rootRef.current;
    if (!sticky || !root || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      root.style.setProperty('--pc-sticky-h', `${Math.ceil(sticky.getBoundingClientRect().height)}px`);
    });
    observer.observe(sticky);
    return () => observer.disconnect();
  }, []);

  // A new box on the desk brings its products up under the scan box.
  const deskRecordId = session?.recordId ?? null;
  useEffect(() => {
    if (!deskRecordId) {
      return;
    }
    const order = rootRef.current?.querySelector<HTMLElement>('.pc-order');
    if (order && typeof order.scrollIntoView === 'function') {
      order.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [deskRecordId]);

  // Each count (or undo) shows the row it landed on, if that row is out of sight; a refused scan leaves the view alone.
  const hitKey = session?.lastKey ?? null;
  const countedUnits = session?.counted.length ?? 0;
  useEffect(() => {
    if (!hitKey) {
      return;
    }
    const row = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('tr[data-key]') ?? []).find(
      (candidate) => candidate.dataset['key'] === hitKey,
    );
    if (row && typeof row.scrollIntoView === 'function') {
      // Instant, so quick scans in a row never stack smooth scrolls on top of each other.
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [hitKey, countedUnits]);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<CheckedOrder[]>(() => readCheckedToday(new Date().toISOString()));
  const [recordingOn, setRecordingOn] = useState<boolean>(() => readZeroPackingEnabled());
  const [recorder, setRecorder] = useState<RecorderState>(() => ({
    status: readZeroPackingEnabled() ? 'IDLE' : 'OFF',
    orderId: null,
    since: null,
    message: null,
  }));
  const [pending, setPending] = useState<PendingPackingRecord[]>(() => readPendingRecords());
  const [linkCheck, setLinkCheck] = useState<LinkCheck>('idle');
  const [nowMs, setNowMs] = useState(() => Date.now());
  const sessionRef = useRef<Session | null>(null);
  const checkedRef = useRef(checked);
  const recordingOnRef = useRef(recordingOn);
  const recorderRef = useRef(recorder);
  const pendingRef = useRef(pending);
  const logRef = useRef<PackingScanLogEntry[]>([]);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const signals = useRef<Promise<unknown>>(Promise.resolve());
  const lastSubmit = useRef<{ code: string; at: number } | null>(null);
  const switchConfirm = useRef<{ orderId: number; at: number } | null>(null);
  const typedSinceSubmit = useRef(false);

  useEffect(() => {
    scanInput.reclaim();
  }, [scanInput]);

  // A click on the Zero Packing mini window takes the keyboard, and the app reads a scan typed there as a start or a
  // stop of its own. A page cannot pull the cursor back from another app, so while the browser is away a cover says
  // so. The click that brings the window back lands on the cover, which gives the scan box the cursor; nothing moves
  // under the pointer, so that click can never press Check or count a unit. The cover then stays a moment, clear, so a
  // double click's second press lands on it too. It lifts by itself only once the scan box has the cursor and the
  // window has the keyboard: with the cursor on a button, a scanner's Enter would press it.
  const [away, setAway] = useState<AwayState>('HERE');
  const awayRef = useRef<AwayState>('HERE');
  const liftTimer = useRef<number | undefined>(undefined);
  const commitAway = useCallback((next: AwayState) => {
    awayRef.current = next;
    setAway(next);
  }, []);
  const liftAway = useCallback(() => {
    if (awayRef.current !== 'AWAY') {
      return;
    }
    commitAway('LIFTING');
    window.clearTimeout(liftTimer.current);
    liftTimer.current = window.setTimeout(() => {
      if (awayRef.current === 'LIFTING') {
        commitAway('HERE');
      }
    }, AWAY_LIFT_MS);
  }, [commitAway]);
  useEffect(() => {
    let settle: number | undefined;
    const deskHasCursor = () =>
      scanInput.ref.current !== null && document.activeElement === scanInput.ref.current && document.hasFocus();
    const onFocus = (event: FocusEvent) => {
      if (event.target !== window) {
        return;
      }
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        if (deskHasCursor()) {
          liftAway();
        }
      }, AWAY_SETTLE_MS);
    };
    const onBlur = (event: FocusEvent) => {
      if (event.target === window) {
        window.clearTimeout(settle);
        window.clearTimeout(liftTimer.current);
        commitAway('AWAY');
      }
    };
    // The page itself (a finished press, a camera read) handed the scan box the cursor while the window has the keyboard.
    const onFocusIn = (event: FocusEvent) => {
      if (event.target === scanInput.ref.current && document.hasFocus()) {
        liftAway();
      }
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('focusin', onFocusIn);
    // A page opened while another app has the keyboard starts covered.
    if (!document.hasFocus()) {
      commitAway('AWAY');
    }
    return () => {
      window.clearTimeout(settle);
      window.clearTimeout(liftTimer.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [scanInput, liftAway, commitAway]);

  const backToDesk = () => {
    if (awayRef.current !== 'AWAY') {
      return;
    }
    liftAway();
    scanInput.reclaim();
  };

  // The running clock on the recording bar; it only ticks while a recording runs.
  useEffect(() => {
    if (recorder.status !== 'RECORDING') {
      return undefined;
    }
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [recorder.status]);

  const commitSession = useCallback((next: Session | null) => {
    sessionRef.current = next;
    setSession(next);
  }, []);

  const commitRecorder = useCallback((next: RecorderState) => {
    recorderRef.current = next;
    setRecorder(next);
    setNowMs(Date.now());
  }, []);

  const commitPending = useCallback((next: PendingPackingRecord[]) => {
    pendingRef.current = next;
    setPending(next);
    writePendingRecords(next);
  }, []);

  /** Notes one read or action against the order on the desk; it goes into the packing record. */
  const addLog = useCallback((result: string, scanned?: string, sku?: string | null) => {
    logRef.current = appendScanLog(logRef.current, {
      at: stamp(),
      result,
      ...(scanned ? { code: scanned.slice(0, 128) } : {}),
      ...(sku ? { sku: sku.slice(0, 64) } : {}),
    });
  }, []);

  /** Changes what the record says about the recording, if the same box is still on the desk. */
  const patchRecording = useCallback(
    (recordId: string, patch: Partial<PackingRecordRecording>) => {
      const current = sessionRef.current;
      if (current && current.recordId === recordId) {
        commitSession({ ...current, recording: { ...current.recording, ...patch } });
      }
    },
    [commitSession],
  );

  /** A fresh video for the box: started (or seen started) now, and no stop on record yet. */
  const markStarted = useCallback(
    (recordId: string, start: 'OK' | 'CONFIRMED_BY_HAND', at: string) => {
      const current = sessionRef.current;
      if (current && current.recordId === recordId) {
        commitSession({ ...current, recording: { enabled: true, start, startedAt: at, stop: 'NOT_SENT' } });
      }
    },
    [commitSession],
  );

  /** A start that did not happen; a box whose earlier video did start keeps saying so. */
  const markStartFailed = useCallback(
    (recordId: string) => {
      const current = sessionRef.current;
      if (!current || current.recordId !== recordId) {
        return;
      }
      if (current.recording.start === 'OK' || current.recording.start === 'CONFIRMED_BY_HAND') {
        return;
      }
      commitSession({ ...current, recording: { ...current.recording, enabled: true, start: 'FAILED' } });
    },
    [commitSession],
  );

  /** One signal at a time, in the order asked, so a stop and the next start never cross. */
  const runSignal = useCallback((body: string): Promise<SignalAnswer> => {
    const next = signals.current.then(() => sendZeroPackingSignal(body));
    signals.current = next.catch(() => undefined);
    return next;
  }, []);

  /** Writes the order into today's list on this PC: packed, checked without a label, or left with units missing. */
  const note = useCallback((current: Session, nowIso: string) => {
    const units = unitsOf(current.rows);
    const next = recordChecked(checkedRef.current, {
      orderId: current.detail.header.orderId,
      at: nowIso,
      scanned: units.scanned,
      expected: units.expected,
      result: current.label ? 'PACKED' : current.completedAt ? 'COMPLETE' : 'LEFT',
    });
    checkedRef.current = next;
    setChecked(next);
    writeCheckedToday(nowIso, next);
  }, []);

  /**
   * Stops whatever recording this screen started or is unsure about. With `always`, the stop is sent even while the
   * screen believes the app waits: `ZEROPACKING-STOP` does nothing on an app that waits, so sending it costs nothing,
   * and the screen's belief can be wrong (a reload, a second tab, a packer who touched the app).
   */
  const stopRecording = useCallback(
    async (always = false): Promise<'OK' | 'FAILED' | 'NOT_SENT'> => {
      const state = recorderRef.current;
      const running = state.status === 'RECORDING' || state.status === 'UNKNOWN';
      if (!running && !(always && state.status !== 'OFF')) {
        return 'NOT_SENT';
      }
      const answer = await runSignal(ZERO_PACKING_STOP);
      if (answer.kind === 'ACCEPTED') {
        commitRecorder({
          status: recordingOnRef.current ? 'IDLE' : 'OFF',
          orderId: null,
          since: stamp(),
          message: null,
        });
        if (running) {
          addLog('RECORDING_STOPPED');
        }
        return 'OK';
      }
      commitRecorder({
        status: 'UNKNOWN',
        orderId: state.orderId,
        since: state.since,
        message: `${describeSignalFailure(answer)} The last recording may still be running: stop it in the Zero Packing mini window, then press Start recording.`,
      });
      addLog('RECORDING_STOP_FAILED', answer.kind === 'REFUSED' ? String(answer.status) : undefined);
      FulfillmentSound.warning();
      return 'FAILED';
    },
    [addLog, commitRecorder, runSignal],
  );

  /**
   * Starts the recording for the box on the desk with its bare order id, which is what Zero Packing keys it by. A
   * bare number stops an app that is already recording, so a stop always goes first and the start only follows an
   * accepted stop; the gap lets the app close the last video before the number arrives.
   */
  const startRecording = useCallback(
    async (target: Session): Promise<SignalAnswer | null> => {
      if (!recordingOnRef.current) {
        return null;
      }
      const stopped = await stopRecording(true);
      if (stopped === 'FAILED') {
        markStartFailed(target.recordId);
        return { kind: 'NO_ANSWER', message: 'the stop before the start got no answer' };
      }
      await pause(ZERO_PACKING_STOP_GAP_MS);
      const orderId = target.detail.header.orderId;
      const answer = await runSignal(String(orderId));
      const at = stamp();
      if (answer.kind === 'ACCEPTED') {
        commitRecorder({ status: 'RECORDING', orderId, since: at, message: null });
        // A fresh video: whatever stop the box had before (by hand, or a failed one) no longer describes it.
        markStarted(target.recordId, 'OK', at);
        addLog('RECORDING_STARTED', String(orderId));
        return answer;
      }
      if (answer.kind === 'REFUSED') {
        // The app answered, so it is known not to record; starting again is safe.
        commitRecorder({ status: 'IDLE', orderId: null, since: at, message: describeSignalFailure(answer) });
        addLog('RECORDING_START_REFUSED', String(answer.status));
      } else {
        commitRecorder({
          status: 'UNKNOWN',
          orderId,
          since: at,
          message: `${describeSignalFailure(answer)} Look at the Zero Packing mini window: is it recording?`,
        });
        addLog('RECORDING_NO_ANSWER');
      }
      markStartFailed(target.recordId);
      FulfillmentSound.warning();
      return answer;
    },
    [addLog, commitRecorder, markStartFailed, markStarted, runSignal, stopRecording],
  );

  /**
   * Keeps the record on the order. It goes into this PC's unsent list before the request leaves and comes out when the
   * server answers, so a tab closed mid-request still has it; the server keeps a resent record once.
   */
  const keepRecord = useCallback(
    async (orderId: number, body: PackingRecordBody): Promise<PackingRecordSaved | null> => {
      const mark = (save: Session['save']) => {
        const current = sessionRef.current;
        if (current && current.recordId === body.recordId) {
          commitSession({ ...current, save });
        }
      };
      commitPending(withPendingRecord(pendingRef.current, { orderId, body, failedAt: stamp(), error: 'Saving…' }));
      try {
        const saved = await client.savePackingRecord(orderId, body);
        commitPending(withoutPendingRecord(pendingRef.current, body.recordId));
        mark('SAVED');
        return saved;
      } catch (err) {
        commitPending(
          withPendingRecord(pendingRef.current, {
            orderId,
            body,
            failedAt: stamp(),
            error: errorText(err),
            permanent: isPermanentRefusal(err),
          }),
        );
        mark('PENDING');
        return null;
      }
    },
    [client, commitPending, commitSession],
  );

  /**
   * Sends the unsent records again, oldest first; the server keeps a record it already has once. A record the server
   * refused outright (a 4xx other than a timeout or a rate limit) is only sent again when the packer asks.
   */
  const sendPendingAgain = useCallback(
    async (includeRefused = false) => {
      for (const entry of [...pendingRef.current].reverse()) {
        if (entry.permanent && !includeRefused) {
          continue;
        }
        try {
          await client.savePackingRecord(entry.orderId, entry.body);
          commitPending(withoutPendingRecord(pendingRef.current, entry.body.recordId));
          const current = sessionRef.current;
          if (current && current.recordId === entry.body.recordId) {
            commitSession({ ...current, save: 'SAVED' });
          }
        } catch (err) {
          commitPending(
            withPendingRecord(pendingRef.current, {
              ...entry,
              failedAt: stamp(),
              error: errorText(err),
              permanent: isPermanentRefusal(err),
            }),
          );
        }
      }
    },
    [client, commitPending, commitSession],
  );

  useEffect(() => {
    if (pendingRef.current.length > 0) {
      void sendPendingAgain();
    }
  }, [sendPendingAgain]);

  // Leaving the page while a recording runs or a record is on its way would orphan the video or the save.
  useEffect(() => {
    const busyLeaving = recorder.status === 'RECORDING' || recorder.status === 'UNKNOWN' || session?.save === 'SAVING';
    if (!busyLeaving) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [recorder.status, session?.save]);

  /** Counts one unit into a row and says so; the last unit of the order says the box is ready to close. */
  const count = useCallback(
    (current: Session, key: string, scanned?: string, how: CountMethod = 'SCAN') => {
      const nowIso = stamp();
      // A cancelled order or one on hold takes no count at all, so it can never read as ready to pack.
      const stop = orderNotices(current.detail, current.rows).find((notice) => notice.tone === 'bad');
      if (stop) {
        setFlash({ tone: 'bad', title: 'Do not pack this order', text: stop.text, code: scanned, at: nowIso });
        addLog('COUNT_REFUSED_BLOCKED', scanned);
        FulfillmentSound.failure();
        return;
      }
      const { rows, result } = countOne(current.rows, key);
      if (result.kind === 'missing') {
        return;
      }
      if (result.kind === 'full') {
        commitSession({ ...current, lastKey: key });
        setFlash({
          tone: 'bad',
          title: `One too many: ${result.row.name}`,
          text: `This order takes ${result.row.expected}, and all ${result.row.expected} are already checked. Take this one out.`,
          code: scanned,
          at: nowIso,
        });
        addLog('ONE_TOO_MANY', scanned, result.row.sku);
        FulfillmentSound.failure();
        return;
      }
      addLog(
        how === 'SCAN' ? 'COUNTED' : how === 'CARD' ? 'COUNTED_NO_BARCODE_CARD' : 'COUNTED_BY_EYE',
        scanned,
        result.row.sku ?? result.row.key,
      );
      const waiting = rows.filter((row) => row.notArrived).length;
      const done = isComplete(rows) && waiting === 0;
      const next: Session = {
        ...current,
        rows,
        counted: [...current.counted, key],
        countedBy: [...current.countedBy, how],
        lastKey: key,
        completedAt: done ? nowIso : null,
      };
      commitSession(next);
      if (done) {
        note(next, nowIso);
        addLog('ALL_CHECKED');
        const units = unitsOf(rows);
        const labelled = outboundLabelCodes(current.detail).length > 0;
        setFlash(
          labelled
            ? {
                tone: 'done',
                title: `All ${units.expected} checked. Pack the box, put the carrier label on and scan it.`,
                code: scanned,
                at: nowIso,
              }
            : {
                tone: 'warn',
                title: `All ${units.expected} checked, but this order has no carrier label yet`,
                text: 'Create the label on the order first, then scan it here to finish. The recording keeps running.',
                code: scanned,
                at: nowIso,
              },
        );
        if (labelled) {
          FulfillmentSound.success();
        } else {
          FulfillmentSound.warning();
        }
        speak('Check complete');
        return;
      }
      if (isComplete(rows)) {
        // Everything is on the desk, but the system still waits for an inbound, so the PACKING sheet cannot print.
        setFlash({
          tone: 'warn',
          title: `All counted, but ${waiting} product${waiting === 1 ? ' is' : 's are'} not inbounded yet`,
          text: 'Inbound them on View Orders first, then pack the box.',
          code: scanned,
          at: nowIso,
        });
        FulfillmentSound.warning();
        return;
      }
      setFlash({
        tone: 'ok',
        title: `${result.row.name} · ${result.row.scanned} / ${result.row.expected}`,
        code: scanned,
        imageUrl: result.row.imageUrl,
        at: nowIso,
      });
      FulfillmentSound.success();
    },
    [addLog, commitSession, note],
  );

  /** The shared no-barcode card: one unit into the order's next product that has no barcode registered. */
  const countNoBarcode = useCallback(
    (current: Session | null, scanned: string) => {
      const nowIso = stamp();
      if (!current) {
        setFlash({ tone: 'warn', title: 'Scan the order number first', code: scanned, at: nowIso });
        FulfillmentSound.warning();
        return;
      }
      const { all, short } = noBarcodeRows(current.rows);
      if (!current.catalogLoaded || all.length === 0) {
        setFlash({
          tone: 'bad',
          title: current.catalogLoaded ? 'Every product of this order has a barcode' : 'Barcodes did not load',
          text: current.catalogLoaded
            ? 'Scan the barcode on the product itself. The no-barcode card is only for a product without one.'
            : 'This screen cannot tell which product has no barcode. Scan the order again.',
          code: scanned,
          at: nowIso,
        });
        addLog('NO_BARCODE_CARD_REFUSED', scanned);
        FulfillmentSound.failure();
        return;
      }
      // Every unit without a barcode is checked by the supervisor's eye; the card only counts them (DY 10/20: two
      // units take two scans), so with several such products it fills them in row order.
      const target = short[0] ?? all[0];
      if (target) {
        count(current, target.key, scanned, 'CARD');
      }
    },
    [addLog, count],
  );

  /** The sample envelope's code: the sample set goes in, as Put in does (DY 10/22). */
  const countSample = useCallback(
    (current: Session | null, scanned: string) => {
      const nowIso = stamp();
      if (!current) {
        setFlash({ tone: 'warn', title: 'Scan the order number first', code: scanned, at: nowIso });
        FulfillmentSound.warning();
        return;
      }
      if (!current.rows.some((row) => row.key === SAMPLE_SET_KEY)) {
        setFlash({
          tone: 'bad',
          title: 'This order takes no sample set',
          text: 'A marketing send goes without samples. Take the sample envelope out.',
          code: scanned,
          at: nowIso,
        });
        addLog('SAMPLE_REFUSED', scanned);
        FulfillmentSound.failure();
        return;
      }
      count(current, SAMPLE_SET_KEY, scanned);
    },
    [addLog, count],
  );

  /**
   * Opens an order on the desk and starts its recording; the order left on the desk is noted and its recording
   * stopped first. With `quietMissing`, a number that names no order says nothing, so the caller can still read it as
   * a product code. `again` packs an order that was already packed here once more.
   */
  const openOrder = useCallback(
    async (
      orderId: number,
      scanned: string,
      quietMissing = false,
      again = false,
    ): Promise<'opened' | 'missing' | 'failed'> => {
      const current = sessionRef.current;
      if (!again && current && current.detail.header.orderId === orderId) {
        setFlash(
          current.label
            ? {
                tone: 'info',
                title: `Order ${orderId} is packed already (${kstClock(current.label.at)}). Scan the next order number.`,
                text: 'To pack it again, press Pack again on the order.',
                code: scanned,
                at: stamp(),
              }
            : {
                tone: 'info',
                title: `Order ${orderId} is already open. Scan its products.`,
                code: scanned,
                at: stamp(),
              },
        );
        return 'opened';
      }
      let detail: FulfillmentOrderDetail;
      try {
        detail = await client.getOrderDetail(orderId);
      } catch (err) {
        const missing = isAxiosError(err) && err.response?.status === 404;
        if (missing && quietMissing) {
          return 'missing';
        }
        setFlash({
          tone: 'bad',
          title: missing ? `No order ${orderId}` : `Could not open order ${orderId}`,
          text: missing ? 'Check the number on the PACKING sheet.' : `${errorText(err)}. Scan it again.`,
          code: scanned,
          at: stamp(),
        });
        FulfillmentSound.failure();
        return missing ? 'missing' : 'failed';
      }
      const skus = [...new Set(detail.lines.map((line) => line.sku))];
      let catalog: Record<string, PackingCatalogEntry> = {};
      let catalogLoaded = true;
      try {
        const products = skus.length > 0 ? await stock.getAdminProductsBySkus(skus) : [];
        catalog = Object.fromEntries(products.map((product) => [product.sku, product]));
      } catch {
        catalogLoaded = false;
      }
      // A box with units already in is not dropped on one scan: the packer scans the other order again to leave it.
      const leaving = !again && current && !current.label && current.counted.length > 0 ? current : null;
      const confirm = switchConfirm.current;
      if (leaving && !(confirm && confirm.orderId === orderId && Date.now() - confirm.at < SWITCH_CONFIRM_MS)) {
        switchConfirm.current = { orderId, at: Date.now() };
        const units = unitsOf(leaving.rows);
        setFlash({
          tone: 'warn',
          title: `Scan order ${orderId} again to leave order ${leaving.detail.header.orderId}`,
          text: `Order ${leaving.detail.header.orderId} has ${units.scanned} / ${units.expected} checked. Scanning ${orderId} again within ${SWITCH_CONFIRM_MS / 1000} s leaves it and opens ${orderId}.`,
          code: scanned,
          at: stamp(),
        });
        FulfillmentSound.warning();
        return 'opened';
      }
      switchConfirm.current = null;
      if (current && !current.label && !current.completedAt && current.counted.length > 0) {
        note(current, stamp());
      }
      const openedAt = stamp();
      const rows = buildCheckRows(detail, catalog);
      const blocked = orderNotices(detail, rows).some((notice) => notice.tone === 'bad');
      const next: Session = {
        detail,
        rows,
        catalogLoaded,
        counted: [],
        countedBy: [],
        lastKey: null,
        completedAt: null,
        recordId: newRecordId(),
        startedAt: openedAt,
        recording: { enabled: recordingOnRef.current, start: 'NOT_SENT', stop: 'NOT_SENT' },
        label: null,
        save: null,
      };
      logRef.current = [
        { at: openedAt, result: blocked ? 'ORDER_BLOCKED' : 'ORDER_OPENED', code: scanned.slice(0, 128) },
      ];
      commitSession(next);
      const units = unitsOf(rows);
      const earlier = checkedRef.current.find((row) => row.orderId === orderId);
      const openTitle = blocked
        ? `Order ${orderId} cannot be packed. Read the red note below.`
        : `Order ${orderId} open: scan its ${units.expected} item${units.expected === 1 ? '' : 's'}.`;
      const openText = earlier
        ? `${earlier.result === 'PACKED' ? 'Packed' : 'Checked'} on this PC earlier at ${kstClock(earlier.at)} (${earlier.scanned} / ${earlier.expected}). Counting starts again from zero.`
        : catalogLoaded
          ? undefined
          : 'Could not load the barcodes, so every scan is checked with the server.';
      if (blocked) {
        // Nothing is packed for this order, but the box left on the desk keeps its own short video.
        await stopRecording();
        setFlash({ tone: 'bad', title: openTitle, text: openText, code: scanned, at: openedAt });
        FulfillmentSound.failure();
        return 'opened';
      }
      if (recordingOnRef.current) {
        setFlash({
          tone: 'info',
          title: `Order ${orderId} open: starting the recording…`,
          text: 'Before the first product, check the Zero Packing mini window says 촬영 중.',
          code: scanned,
          at: openedAt,
        });
      }
      if (!recordingOnRef.current) {
        // Off on this PC, but a recording left running or unsure is still stopped rather than run into this box.
        await stopRecording();
      }
      const answer = await startRecording(next);
      if (answer && answer.kind !== 'ACCEPTED') {
        setFlash({
          tone: 'warn',
          title: `Order ${orderId} open, but the recording did not start`,
          text:
            recorderRef.current.message ??
            `${describeSignalFailure(answer)} See the recording bar above before you pack.`,
          code: scanned,
          at: stamp(),
        });
        return 'opened';
      }
      setFlash({ tone: 'info', title: openTitle, text: openText, code: scanned, at: openedAt });
      if (earlier || !catalogLoaded) {
        FulfillmentSound.warning();
      }
      return 'opened';
    },
    [client, commitSession, note, startRecording, stock, stopRecording],
  );

  /**
   * The carrier label scanned off the box: the box is closed, the recording stops and the record is kept. `label` is
   * the order's own label number the scan matched, so the server compares the same characters the tracking row holds.
   */
  const takeLabel = useCallback(
    async (current: Session, scanned: string, label: string) => {
      const nowIso = stamp();
      const orderId = current.detail.header.orderId;
      if (!current.completedAt) {
        const units = unitsOf(current.rows);
        const waiting = current.rows.filter((row) => row.notArrived).length;
        setFlash(
          isComplete(current.rows) && waiting > 0
            ? {
                tone: 'warn',
                title: `Every unit is counted, but ${waiting} product${waiting === 1 ? ' is' : 's are'} not inbounded yet`,
                text: 'Inbound them on View Orders first; the box can be closed after that.',
                code: scanned,
                at: nowIso,
              }
            : {
                tone: 'warn',
                title: `Finish the products first: ${units.scanned} / ${units.expected}`,
                text: 'The carrier label is scanned last, once every product is in the box.',
                code: scanned,
                at: nowIso,
              },
        );
        addLog('LABEL_TOO_EARLY', scanned);
        FulfillmentSound.warning();
        return;
      }
      addLog('LABEL_MATCHED', scanned);
      // The box is closed the moment its label is read, before the stop goes out, so nothing can count into it meanwhile.
      const closed: Session = { ...current, label: { code: label, at: nowIso }, save: 'SAVING' };
      commitSession(closed);
      note(closed, nowIso);
      const stoppedByHand =
        current.recording.stop === 'BY_HAND' &&
        (recorderRef.current.status === 'IDLE' || recorderRef.current.status === 'OFF');
      // Sent even when the screen believes nothing records: it does nothing then, and the belief can be wrong.
      const sent =
        recordingOnRef.current || recorderRef.current.status !== 'OFF' ? await stopRecording(true) : 'NOT_SENT';
      const stop: PackingStopSignal = stoppedByHand ? 'BY_HAND' : sent;
      const latest =
        sessionRef.current && sessionRef.current.recordId === current.recordId ? sessionRef.current : closed;
      const packed: Session = {
        ...latest,
        recording: { ...latest.recording, stop, ...(sent === 'OK' && !stoppedByHand ? { stoppedAt: stamp() } : {}) },
      };
      commitSession(packed);
      const saved = await keepRecord(orderId, recordBodyOf(packed, logRef.current));
      const stopText =
        stop === 'OK'
          ? 'Stop sent to Zero Packing.'
          : stop === 'FAILED'
            ? 'The recording may still be running: stop it in the Zero Packing mini window.'
            : stop === 'BY_HAND'
              ? 'The recording was stopped earlier by hand.'
              : 'Packed without Zero Packing.';
      const shared =
        saved && saved.sharedWith.length > 0
          ? ` This label also ships order${saved.sharedWith.length === 1 ? '' : 's'} ${saved.sharedWith.join(', ')}.`
          : '';
      if (saved && !saved.labelMatched) {
        setFlash({
          tone: 'bad',
          title: `Order ${orderId}: the server does not read ${label} as this order's carrier label`,
          text: `${stopText} The record is kept, marked as not matching. Check the label on the box before it leaves.`,
          code: scanned,
          at: nowIso,
        });
        FulfillmentSound.failure();
      } else if (saved) {
        setFlash({
          tone: stop === 'FAILED' ? 'warn' : 'done',
          title: `Order ${orderId} packed. Scan the next order number.`,
          text: `${stopText} The packing record is saved on the order.${shared}`,
          code: scanned,
          at: nowIso,
        });
        if (stop === 'FAILED') {
          FulfillmentSound.warning();
        } else {
          FulfillmentSound.success();
        }
      } else {
        setFlash({
          tone: 'warn',
          title: `Order ${orderId} packed, but the record is not saved yet`,
          text: `${stopText} The record is kept on this PC and sent again: see Not saved yet below.`,
          code: scanned,
          at: nowIso,
        });
        FulfillmentSound.warning();
      }
      speak('Packed');
    },
    [addLog, commitSession, keepRecord, note, stopRecording],
  );

  /** Reads the open order again for its carrier labels; the rows and counts on the desk stay as they are. */
  const refreshLabels = useCallback(
    async (current: Session): Promise<Session | null> => {
      try {
        const detail = await client.getOrderDetail(current.detail.header.orderId);
        const latest = sessionRef.current;
        if (!latest || latest.recordId !== current.recordId) {
          return null;
        }
        const next: Session = { ...latest, detail: { ...latest.detail, tracking: detail.tracking } };
        commitSession(next);
        return next;
      } catch {
        return null;
      }
    },
    [client, commitSession],
  );

  const handle = useCallback(
    async (scanned: string) => {
      const current = sessionRef.current;
      // The order still being packed; a packed one takes nothing but the next order number.
      const open = current && !current.label ? current : null;
      if (codeKey(scanned) === NO_BARCODE_CODE) {
        countNoBarcode(open, scanned);
        return;
      }
      if (codeKey(scanned) === SAMPLE_SET_CODE) {
        countSample(open, scanned);
        return;
      }
      if (!open) {
        if (looksLikeOrderNumber(scanned)) {
          await openOrder(Number(scanned), scanned);
          return;
        }
        // Step 1 takes the order number only: it opens the order and names the video.
        setFlash({
          tone: 'warn',
          title: current?.label
            ? `Order ${current.detail.header.orderId} is packed. Scan the next order number.`
            : 'That is not an order number',
          text: 'Start with the order number on the PACKING sheet. The products and the carrier label come after it.',
          code: scanned,
          at: stamp(),
        });
        FulfillmentSound.warning();
        return;
      }
      const ownLabel = matchedLabel(open.detail, scanned);
      if (ownLabel) {
        await takeLabel(open, scanned, ownLabel);
        return;
      }
      // A product of the order on the desk, found by its barcode without asking the server.
      const named = rowsByCode(open.rows, scanned);
      if (named.length > 1) {
        setFlash({
          tone: 'bad',
          title: `${named.length} products of this order share this barcode`,
          text: 'The scan cannot tell them apart. Check the product by eye and press Count 1 by eye on its row.',
          code: scanned,
          at: stamp(),
        });
        addLog('SHARED_BARCODE', scanned);
        FulfillmentSound.failure();
        return;
      }
      const local = named[0];
      if (local) {
        count(open, local.key, scanned);
        return;
      }
      // A run of digits shaped like an order number opens that order; a number that names no order goes on to be
      // read as a product code (a ten-digit sku exists).
      if (looksLikeOrderNumber(scanned)) {
        const opened = await openOrder(Number(scanned), scanned, true);
        if (opened !== 'missing') {
          return;
        }
      }
      // A label made after the order was opened is not in the order the screen holds; read the order again once.
      const fresh = open.completedAt ? await refreshLabels(open) : null;
      const freshLabel = fresh ? matchedLabel(fresh.detail, scanned) : null;
      if (fresh && freshLabel) {
        await takeLabel(fresh, scanned, freshLabel);
        return;
      }
      let answer;
      try {
        answer = await client.scanCode({ code: scanned });
      } catch (err) {
        setFlash({
          tone: 'bad',
          title: 'Could not read that barcode',
          text: `${errorText(err)}. Scan it again.`,
          code: scanned,
          at: stamp(),
        });
        addLog('READ_FAILED', scanned);
        FulfillmentSound.failure();
        return;
      }
      const nowIso = stamp();
      const desk = sessionRef.current && !sessionRef.current.label ? sessionRef.current : open;
      const deskOrderId = desk.detail.header.orderId;
      if (answer.kind === 'PRODUCT_BARCODE' && answer.sku) {
        const row = desk.rows.find((candidate) => !candidate.sampleSet && candidate.sku === answer.sku);
        if (row) {
          count(desk, row.key, scanned);
          return;
        }
        setFlash({
          tone: 'bad',
          title: `Not in this order: ${answer.productName ?? answer.sku}`,
          text: `SKU ${answer.sku}. Take it out of the box.`,
          code: scanned,
          at: nowIso,
        });
        addLog('NOT_IN_ORDER', scanned, answer.sku);
        FulfillmentSound.failure();
        return;
      }
      if (answer.kind === 'RETURN_WAYBILL') {
        // The server reads it as an outbound label. Only its own pick counts: `alsoMatched` mixes every tracking kind,
        // so a shared label of this order was already caught by the order read above.
        if (answer.orderId === deskOrderId) {
          await takeLabel(desk, scanned, answer.trackingNumber ?? scanned);
          return;
        }
        setFlash({
          tone: 'bad',
          title: `That label is order ${answer.orderId ?? '?'}'s, not ${deskOrderId}`,
          text: 'Wrong box or wrong label. Put the label of this order on the box and scan it.',
          code: scanned,
          at: nowIso,
        });
        addLog('WRONG_LABEL', scanned);
        FulfillmentSound.failure();
        return;
      }
      if (answer.kind === 'ORDER_WAYBILL' && answer.orderId != null) {
        const ownWaybill = answer.orderId === deskOrderId;
        setFlash({
          tone: 'bad',
          title: ownWaybill
            ? `That is a supplier waybill of order ${deskOrderId}, not ${desk.completedAt ? 'the carrier label' : 'a product'}`
            : `That label belongs to order ${answer.orderId}, not ${deskOrderId}`,
          text: desk.completedAt ? 'Scan the carrier label on the box.' : NOT_A_PRODUCT_HINT,
          code: scanned,
          at: nowIso,
        });
        addLog(ownWaybill ? 'SUPPLIER_WAYBILL' : 'WRONG_LABEL', scanned);
        FulfillmentSound.failure();
        return;
      }
      setFlash({
        tone: 'bad',
        title: answer.kind === 'UNKNOWN' ? 'Not in this order' : 'Not a product barcode',
        text:
          answer.kind === 'UNKNOWN'
            ? desk.completedAt
              ? 'Neither a product nor a carrier label of this order. To finish, scan the carrier label on the box.'
              : 'No product of this order has this barcode, and the catalog does not name it either. Take it out. If it really is one of the products above, its barcode is not registered: press Count 1 by eye on its row.'
            : NOT_A_PRODUCT_HINT,
        code: scanned,
        at: nowIso,
      });
      addLog(answer.kind === 'UNKNOWN' ? 'UNKNOWN_CODE' : 'NOT_A_PRODUCT', scanned);
      FulfillmentSound.failure();
    },
    [addLog, client, count, countNoBarcode, countSample, openOrder, refreshLabels, takeLabel],
  );

  /** Runs one desk action after the ones before it, so each reads the state the last one left. */
  const enqueue = useCallback(
    (work: () => Promise<void> | void) => {
      setBusy(true);
      queue.current = queue.current
        .then(work)
        .catch(() => undefined)
        .finally(() => {
          setBusy(false);
          scanInput.reclaim();
        });
    },
    [scanInput],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const scanned = scannedCode(code);
    if (!scanned) {
      return;
    }
    // A repeat of the untouched code straight after is the scanner's second line terminator, not a second unit.
    const now = Date.now();
    const last = lastSubmit.current;
    if (last && last.code === scanned && !typedSinceSubmit.current && now - last.at < SCAN_SETTLED_MS) {
      scanInput.settle();
      return;
    }
    lastSubmit.current = { code: scanned, at: now };
    typedSinceSubmit.current = false;
    scanInput.settle();
    // One at a time in the order scanned, so each count reads the one before it.
    enqueue(() => handle(scanned));
  };

  const undo = () => {
    const current = sessionRef.current;
    if (!current || current.label || current.counted.length === 0) {
      return;
    }
    const key = current.counted[current.counted.length - 1];
    if (key === undefined) {
      return;
    }
    const rows = uncountOne(current.rows, key);
    const row = rows.find((candidate) => candidate.key === key);
    commitSession({
      ...current,
      rows,
      counted: current.counted.slice(0, -1),
      countedBy: current.countedBy.slice(0, -1),
      lastKey: key,
      completedAt: null,
    });
    addLog('UNDO', undefined, row?.sku ?? key);
    setFlash({
      tone: 'warn',
      title: `Took one back: ${row?.name ?? key}`,
      text: row ? `${row.scanned} / ${row.expected}` : undefined,
      at: stamp(),
    });
    scanInput.reclaim();
  };

  const startOver = () => {
    const current = sessionRef.current;
    if (!current || current.label) {
      return;
    }
    commitSession({
      ...current,
      rows: current.rows.map((row) => ({ ...row, scanned: 0 })),
      counted: [],
      countedBy: [],
      lastKey: null,
      completedAt: null,
    });
    addLog('START_OVER');
    setFlash({
      tone: 'warn',
      title: `Order ${current.detail.header.orderId}: counting from zero`,
      at: stamp(),
    });
    scanInput.reclaim();
  };

  const countByHand = (key: string) => {
    const current = sessionRef.current;
    if (current && !current.label) {
      count(current, key, undefined, 'EYE');
    }
    scanInput.reclaim();
  };

  /**
   * The box a button was pressed for. A press waits behind earlier scans in the queue, so when it runs the desk may
   * hold another order; then it does nothing rather than act on a box the packer did not mean.
   */
  const deskAtPress = () => sessionRef.current?.recordId ?? null;
  const stillDesk = (recordId: string | null): Session | null => {
    const current = sessionRef.current;
    return current && current.recordId === recordId && !current.label ? current : null;
  };

  /** The packer saw the mini window recording this order after its start got no answer. */
  const confirmRecording = () => {
    const pressed = deskAtPress();
    enqueue(() => {
      const current = stillDesk(pressed);
      if (
        !current ||
        recorderRef.current.status !== 'UNKNOWN' ||
        recorderRef.current.orderId !== current.detail.header.orderId
      ) {
        return;
      }
      const at = stamp();
      commitRecorder({ status: 'RECORDING', orderId: current.detail.header.orderId, since: at, message: null });
      markStarted(current.recordId, 'CONFIRMED_BY_HAND', at);
      addLog('RECORDING_CONFIRMED_BY_HAND');
    });
  };

  /** Starts the recording for the open order; the start itself sends a stop first, so it cannot land as a stop. */
  const restartRecording = () => {
    const pressed = deskAtPress();
    enqueue(async () => {
      const current = stillDesk(pressed);
      if (!current || !recordingOnRef.current || recorderRef.current.status === 'RECORDING') {
        return;
      }
      const answer = await startRecording(current);
      if (answer?.kind === 'ACCEPTED') {
        setFlash({ tone: 'info', title: `Start sent for order ${current.detail.header.orderId}`, at: stamp() });
      }
    });
  };

  const stopByHand = () => {
    const pressed = deskAtPress();
    enqueue(async () => {
      // Another box came onto the desk since the press: its new video is not the one the packer meant to stop.
      if ((sessionRef.current?.recordId ?? null) !== pressed) {
        return;
      }
      const current = stillDesk(pressed);
      const stopped = await stopRecording();
      if (current && stopped === 'OK') {
        patchRecording(current.recordId, { stop: 'BY_HAND', stoppedAt: stamp() });
      }
    });
  };

  /**
   * Asks the app whether its link is up (GET / starts and stops nothing). Only the latest ask may write its answer, so
   * an older one held by the browser's prompt can never cover a newer one.
   */
  const linkAsk = useRef(0);
  const checkLink = useCallback(() => {
    const ask = ++linkAsk.current;
    setLinkCheck('checking');
    void probeZeroPacking().then((up) => {
      if (ask === linkAsk.current) {
        setLinkCheck(up ? 'up' : 'down');
      }
    });
  }, []);

  const toggleRecording = (enabled: boolean) =>
    enqueue(async () => {
      if (!enabled) {
        const current = sessionRef.current;
        const stopped = await stopRecording();
        if (current && !current.label && stopped === 'OK') {
          patchRecording(current.recordId, { stop: 'BY_HAND', stoppedAt: stamp() });
        }
      }
      recordingOnRef.current = enabled;
      setRecordingOn(enabled);
      writeZeroPackingEnabled(enabled);
      if (enabled) {
        checkLink();
      } else {
        // Off here: an answer still on its way says nothing the packer needs.
        linkAsk.current += 1;
        setLinkCheck('idle');
      }
      if (recorderRef.current.status === 'UNKNOWN') {
        // A signal that got no answer stays visible either way, so the packer still checks the mini window.
        return;
      }
      commitRecorder({ status: enabled ? 'IDLE' : 'OFF', orderId: null, since: null, message: null });
    });

  // Asking once on arrival brings the browser's local-network prompt, or a closed app, before the first box rather
  // than in the middle of one.
  useEffect(() => {
    if (recordingOnRef.current) {
      checkLink();
    }
  }, [checkLink]);

  const packAgain = () =>
    enqueue(async () => {
      const current = sessionRef.current;
      if (current?.label) {
        await openOrder(current.detail.header.orderId, String(current.detail.header.orderId), false, true);
      }
    });

  const header = session?.detail.header;
  const notices = session ? orderNotices(session.detail, session.rows) : [];
  const blocked = notices.some((notice) => notice.tone === 'bad');
  const units = session ? unitsOf(session.rows) : null;
  const complete = session ? session.completedAt !== null : false;
  const packed = session?.label != null;
  const shared = session ? sharedBarcodeKeys(session.rows) : new Set<string>();
  const labels = session ? outboundLabelCodes(session.detail) : [];
  const deskOpen = session !== null && !packed && !blocked;
  const startedOk = session?.recording.start === 'OK' || session?.recording.start === 'CONFIRMED_BY_HAND';
  // The box whose save is on its way is shown by its own line, not as unsent.
  const unsent = pending.filter((entry) => !(session?.save === 'SAVING' && entry.body.recordId === session.recordId));

  const recorderTitle =
    recorder.status === 'OFF'
      ? 'Zero Packing is off on this PC'
      : recorder.status === 'RECORDING'
        ? `Start sent for order ${recorder.orderId ?? ''} · ${elapsed(recorder.since, nowMs)}`
        : recorder.status === 'UNKNOWN'
          ? 'Recording: not sure'
          : 'Not recording';
  const recorderNote =
    recorder.message ??
    (recorder.status === 'OFF'
      ? 'Boxes are checked and the record is kept on the order, without a video.'
      : recorder.status === 'IDLE'
        ? 'The order number starts the recording; the carrier label stops it.'
        : recorder.status === 'RECORDING'
          ? // The app answers 201 even when it drops the start (not signed in, no camera): only its window shows it.
            'Check the Zero Packing mini window says 촬영 중 with this order. If it does not, press Stop recording, fix the app, then Start recording.'
          : null);

  return (
    <div
      ref={rootRef}
      className={`wms-wireframe ff-ultimate wms-packing-check${session && !packed ? ' pc-desk-open' : ''}`}
    >
      <div className="ptitle">
        <h2>WMS - Packing</h2>
        <p className="sub">
          Scan the order number to start the recording, then every product. Pack the box, put the carrier label on and
          scan it: the recording stops and the packing record is kept on the order.
        </p>
      </div>

      <PackingSteps session={session} recorder={recorder} />

      <div
        className={`pc-rec pc-rec-${recorder.status.toLowerCase()}`}
        data-testid="packing-recorder"
        data-status={recorder.status}
      >
        <span className="pc-rec-dot" aria-hidden="true">
          <VideoIcon />
        </span>
        <div className="pc-rec-body">
          <b>{recorderTitle}</b>
          {recorderNote && <span>{recorderNote}</span>}
        </div>
        <div className="pc-rec-actions">
          {recorder.status === 'UNKNOWN' && deskOpen && !startedOk && recorder.orderId === header?.orderId && (
            <button type="button" className="btn btn-line btn-sm" onClick={confirmRecording}>
              It is recording
            </button>
          )}
          {deskOpen && recordingOn && (recorder.status === 'IDLE' || recorder.status === 'UNKNOWN') && (
            <button type="button" className="btn btn-blue btn-sm" onClick={restartRecording}>
              <VideoIcon />
              Start recording
            </button>
          )}
          {(recorder.status === 'RECORDING' || recorder.status === 'UNKNOWN') && (
            <button type="button" className="btn btn-line btn-sm" onClick={stopByHand}>
              Stop recording
            </button>
          )}
        </div>
      </div>
      <div className="pc-rec-settings">
        <label>
          <input
            type="checkbox"
            checked={recordingOn}
            onChange={(event) => toggleRecording(event.target.checked)}
            aria-label="Start and stop Zero Packing from this screen"
          />
          Start and stop Zero Packing from this screen
        </label>
        <button type="button" className="btn btn-line btn-sm" onClick={checkLink} disabled={linkCheck === 'checking'}>
          <RefreshIcon />
          Test connection
        </button>
        <span className={`pc-link pc-link-${linkCheck}`} data-testid="packing-link-check">
          {linkCheck === 'checking'
            ? 'Asking the Zero Packing app…'
            : linkCheck === 'up'
              ? 'Zero Packing answered on this PC (mini mode is on).'
              : linkCheck === 'down'
                ? 'No answer. Open Zero Packing and turn mini mode on. If the browser asks to reach devices on the local network, allow it (if it was blocked, allow it in the site settings left of the address), then press Test connection.'
                : 'Zero Packing must run on this PC in mini mode.'}
        </span>
      </div>

      {away !== 'HERE' && (
        // The cover takes the returning click itself; keeping the cursor where it was on that press means the scan box
        // still has it when the cover lifts.
        <button
          type="button"
          className={`pc-away${away === 'LIFTING' ? ' pc-away-lifting' : ''}`}
          data-testid="packing-cursor-away"
          data-state={away}
          tabIndex={-1}
          aria-hidden={away === 'LIFTING' ? true : undefined}
          onMouseDown={(event) => event.preventDefault()}
          onClick={backToDesk}
        >
          {away === 'AWAY' && (
            <span className="pc-away-card" role="alert">
              <b>This screen is not taking scans.</b>
              <span>
                Click here before the next scan. A scan typed into the Zero Packing mini window starts or stops its
                recording by itself.
              </span>
            </span>
          )}
        </button>
      )}

      <div className="pc-sticky" ref={stickyRef}>
        <form className="pc-scan" onSubmit={submit}>
          <label className="pc-scan-label" htmlFor="packing-check-code">
            Scan
          </label>
          <div className="pc-scan-row">
            <div className="scanwrap">
              <ScanIcon className="scan-glyph" />
              <CameraScanField label="Scan with camera" className="pc-scan-field" ultimate>
                <input
                  id="packing-check-code"
                  ref={scanInput.ref}
                  aria-label="Order number, product barcode or carrier label"
                  {...asciiScanField((value) => {
                    scanInput.noteTyping();
                    typedSinceSubmit.current = true;
                    setCode(value);
                  })}
                  value={code}
                  onClick={scanInput.reclaim}
                  placeholder={
                    !session || packed
                      ? 'Order number on the PACKING sheet'
                      : complete
                        ? 'Carrier label on the box'
                        : 'Each product'
                  }
                  // Password managers ignore autoComplete="off"; their pop-up would swallow the scanner's keystrokes.
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
              </CameraScanField>
            </div>
            {/* Never disabled: a disabled default button makes the browser swallow the scanner's Enter. */}
            <button type="submit" className="btn btn-blue">
              <CheckIcon />
              Check
            </button>
          </div>
        </form>

        <div aria-live="polite" className={busy ? 'pc-busy' : undefined}>
          <FlashLine flash={flash} />
        </div>
      </div>

      {session && header && units && (
        <section
          className={`pc-order${complete ? ' pc-order-done' : ''}${packed ? ' pc-order-packed' : ''}`}
          aria-label={`Order ${header.orderId}`}
        >
          <div className="pc-ordercard">
            <div className="pc-oc-item">
              <span className="pc-oc-label">Order ID</span>
              <Link
                className="oid"
                target="_blank"
                rel="noopener noreferrer"
                to={ROUTES.fulfillment.viewOrdersOrder(header.orderId)}
              >
                {header.orderId}
              </Link>
            </div>
            <div className="pc-oc-item">
              <span className="pc-oc-label">Fulfillment</span>
              <span className="pc-oc-value">{header.fulfillmentStatus}</span>
            </div>
            <div className="pc-oc-item">
              <span className="pc-oc-label">Country</span>
              <span className="pc-oc-value">{header.countryCode ?? '—'}</span>
            </div>
            <div className="pc-oc-item">
              <span className="pc-oc-label">Shelf</span>
              <span className="pc-oc-value">{header.temporaryLocation || '—'}</span>
            </div>
            <div className="pc-oc-item pc-oc-progress" data-testid="packing-progress">
              <span className="pc-oc-label">Checked</span>
              <span className="pc-oc-count">
                {units.scanned} / {units.expected}
              </span>
            </div>
            <div className="pc-oc-actions">
              {packed ? (
                <button type="button" className="btn btn-line" onClick={packAgain}>
                  <RefreshIcon />
                  Pack again
                </button>
              ) : (
                <>
                  <button type="button" className="btn btn-line" onClick={undo} disabled={session.counted.length === 0}>
                    Undo last
                  </button>
                  <button type="button" className="btn btn-line" onClick={startOver} disabled={units.scanned === 0}>
                    <RefreshIcon />
                    Start over
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="pc-labelline" data-testid="packing-label">
            <span className="pc-oc-label">Carrier label</span>
            {session.label ? (
              <>
                <code className="pc-label-code">{session.label.code}</code>
                <span className="pc-label-at">scanned {kstClock(session.label.at)}</span>
                <span
                  className={`pc-save pc-save-${(session.save ?? 'SAVING').toLowerCase()}`}
                  data-testid="packing-save"
                >
                  {session.save === 'SAVED'
                    ? 'Record saved on the order'
                    : session.save === 'PENDING'
                      ? 'Record not saved yet'
                      : 'Saving the record…'}
                </span>
                {session.save === 'SAVED' && (
                  <Link
                    className="pc-record-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    to={`${ROUTES.fulfillment.orderDetail(header.orderId)}#packing-records`}
                  >
                    See it on Order Detail
                  </Link>
                )}
              </>
            ) : labels.length > 0 ? (
              <span className="pc-label-hint">
                {complete ? 'Scan it now: ' : 'Scan this last: '}
                <code>{labels.join(' · ')}</code>
              </span>
            ) : (
              <span className="pc-label-hint pc-label-none">
                No carrier label on this order yet. Create it on the order before the box is closed.
              </span>
            )}
          </div>

          {notices.length > 0 && (
            <ul className="pc-notices">
              {notices.map((notice) => (
                <li key={notice.text} className={`pc-notice pc-notice-${notice.tone}`} role="alert">
                  <WarningIcon />
                  {notice.text}
                </li>
              ))}
            </ul>
          )}

          <div className="tbl-scroll">
            <table className="tbl" aria-label="Products to pack">
              <colgroup>
                <col className="col-state" />
                <col className="col-image pc-wide" />
                <col className="col-name" />
                <col className="col-sku pc-wide" />
                <col className="col-barcode" />
                <col className="col-count" />
                <col className="col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th aria-label="Checked" />
                  <th aria-label="Image" className="pc-wide" />
                  <th>Product</th>
                  <th className="pc-wide">SKU</th>
                  <th>Barcode</th>
                  <th className="num">Count</th>
                  <th aria-label="Action" />
                </tr>
              </thead>
              <tbody>
                {session.rows.map((row) => {
                  const done = row.scanned >= row.expected;
                  const byHand =
                    row.sampleSet || (session.catalogLoaded && row.barcode === null) || shared.has(row.key);
                  return (
                    <tr
                      key={row.key}
                      data-testid="packing-row"
                      data-key={row.key}
                      className={[done ? 'row-done' : '', session.lastKey === row.key ? 'row-hit' : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <td>
                        <RowState row={row} />
                      </td>
                      <td className="pc-wide">
                        {row.imageUrl ? (
                          <img className="pc-thumb" src={row.imageUrl} alt="" loading="lazy" />
                        ) : (
                          <span className="pc-thumb pc-thumb-none" />
                        )}
                      </td>
                      <td>
                        <div className="pc-name">{row.name}</div>
                        {row.size && <div className="pc-sub">{row.size}</div>}
                        {row.notArrived && <div className="pc-sub pc-sub-warn">Not inbounded yet</div>}
                      </td>
                      <td className="pc-mono pc-wide">{row.sku ?? '—'}</td>
                      <td className="pc-mono">
                        {row.sampleSet ? (
                          SAMPLE_SET_CODE
                        ) : row.barcode ? (
                          row.barcode
                        ) : session.catalogLoaded ? (
                          <span className="pc-nobarcode">No barcode</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="num">
                        <span className={`pc-count${done ? ' pc-count-done' : ''}`}>
                          {row.scanned} / {row.expected}
                        </span>
                      </td>
                      <td>
                        {byHand && (
                          <button
                            type="button"
                            className="btn btn-sm btn-line"
                            disabled={done || blocked || packed}
                            onClick={() => countByHand(row.key)}
                          >
                            {row.sampleSet ? 'Put in' : 'Count 1 by eye'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {unsent.length > 0 && (
        <section className="pc-pending" aria-label="Not saved yet">
          <div className="psub">
            <h3>Not saved yet</h3>
            <span className="sp" />
            <span className="pc-pending-n">{unsent.length}</span>
            <button type="button" className="btn btn-line btn-sm" onClick={() => enqueue(() => sendPendingAgain(true))}>
              Send again
            </button>
          </div>
          <p className="pc-pending-note">
            These boxes are packed, but their records have not reached the server. They are kept on this PC and sent
            again when this screen opens. Do not clear this browser&apos;s data until the list is empty.
          </p>
          <ul>
            {unsent.map((entry) => (
              <li key={entry.body.recordId}>
                <Link
                  className="oid"
                  target="_blank"
                  rel="noopener noreferrer"
                  to={ROUTES.fulfillment.viewOrdersOrder(entry.orderId)}
                >
                  {entry.orderId}
                </Link>
                <span>packed {kstClock(entry.body.labelScannedAt)}</span>
                <span className="pc-pending-error">
                  {entry.permanent ? `Refused by the server: ${entry.error}. Tell the supervisor.` : entry.error}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="psub pc-today-head">
        <h3>Packed today on this PC</h3>
        <span className="sp" />
        <span className="pc-today-n">{checked.length}</span>
      </div>
      {checked.length === 0 ? (
        <p className="pc-empty">No order packed yet today.</p>
      ) : (
        <ul className="pc-today" aria-label="Packed today">
          {checked.slice(0, 30).map((row) => (
            <li key={row.orderId}>
              <span
                className={`pc-today-mark ${row.result === 'PACKED' ? 'ok' : row.result === 'COMPLETE' ? 'checked' : 'left'}`}
              >
                {row.result === 'PACKED' ? 'Packed' : row.result === 'COMPLETE' ? 'Checked' : 'Left'}
              </span>
              <Link
                className="oid"
                target="_blank"
                rel="noopener noreferrer"
                to={ROUTES.fulfillment.viewOrdersOrder(row.orderId)}
              >
                {row.orderId}
              </Link>
              <span className="pc-today-units">
                {row.scanned} / {row.expected}
              </span>
              <span className="pc-today-at">{kstClock(row.at)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="pc-cards">
        <ScanCard code={NO_BARCODE_CODE} title="No-barcode card" onUsed={scanInput.reclaim}>
          For a product with no barcode of its own: the supervisor checks the product by eye and scans this card once
          per unit, so two units take two scans. It counts into the order&apos;s products that have no barcode
          registered. Print it and keep it at the desk.
        </ScanCard>
        <ScanCard code={SAMPLE_SET_CODE} title="Sample envelope card" onUsed={scanInput.reclaim}>
          Scanning this puts the sample set in, the same as pressing Put in. Until the sample envelopes carry it, print
          this card and stick one on each envelope.
        </ScanCard>
      </div>
      <p className="pc-foot">
        This screen never changes inbound, outbound, location or status. Its only write is the packing record kept on
        the order when the carrier label is scanned.
      </p>
    </div>
  );
}
